import { api, type Api } from "@api/bot-base-api-request";
import { useMutation } from "@tanstack/react-query";

/**
 * Mutation key for deleting copy trade settings
 */
export const BOT_DELETE_COPY_TRADE_QUERY_KEY = "bot-delete-copy-trade";

/**
 * Parameters for deleting copy trade settings
 * @typedef {Object} BotDeleteCopyTradeParams
 * @property {string} userId - User's unique identifier
 * @property {string} walletAddress - User's wallet address
 * @property {string} copyWallet - The wallet address to remove from copy list
 * @property {number} chainId - ID of the blockchain network
 */
export type BotDeleteCopyTradeParams = {
  userId: string;
  walletAddress: string;
  copyWallet: string;
  chainId: number;
};

/**
 * Response structure for copy trade deletion
 * @typedef {Object} UpdateCopyTradeResponse
 * @property {string} message - Success or error message
 * @property {Array<Object>} copyTradeData - Updated array of wallets being copied
 */
export type UpdateCopyTradeResponse = {
  message: string;
  copyTradeData: Array<{
    address: string;
    nickname?: string;
    amountPercent?: number;
    isDuplicateBuy?: boolean;
    slippage?: number;
    tokenRenounced?: boolean;
    tokenMinLiquidity?: number;
    tokenMaxLiquidity?: number;
    tokenMinMC?: number;
    tokenMaxMC?: number;
    tokenMaxBuyTax?: number;
    tokenMaxSellTax?: number;
    copySell?: boolean;
    isOn?: boolean;
    useProfile?: boolean;
    createAt?: number;
    isBuyWithFixedAmount?: boolean;
    amount?: number;
    isAutoSlippage?: boolean;
  }>;
};

/**
 * Removes a wallet from the copy trade list
 *
 * @param {Api} api - API instance
 * @param {BotDeleteCopyTradeParams} params - Parameters for the API request
 * @returns {Promise<UpdateCopyTradeResponse>} Promise that resolves to update response
 */
export const postBotDeleteCopyTrade = async (
  api: Api,
  params: BotDeleteCopyTradeParams
): Promise<UpdateCopyTradeResponse> =>
  api.post("copyTrade/delete", { json: params }).json();

/**
 * React Query mutation hook for deleting copy trade settings
 *
 * @returns {UseMutationResult<UpdateCopyTradeResponse, Error, BotDeleteCopyTradeParams>} React Query mutation result
 *
 * @example
 * // Use mutation to delete a wallet from copy list
 * const deleteMutation = useBotDeleteCopyTradeMutation();
 *
 * // Later in your code:
 * deleteMutation.mutate({
 *   userId: "user123",
 *   walletAddress: "0x123...abc",
 *   copyWallet: "0xdef...456",
 *   chainId: 1
 * });
 */
export const useBotDeleteCopyTradeMutation = () =>
  useMutation({
    mutationFn: async (params: BotDeleteCopyTradeParams) =>
      postBotDeleteCopyTrade(api, params),
    mutationKey: [BOT_DELETE_COPY_TRADE_QUERY_KEY],
  });
